// Single-memory fetch hook. Shares the same process-lifetime LRU as
// useEntity but hits /api/memory/{urn} for memory/episode URNs.

import { useState, useEffect } from 'react';

const MAX = 200;
const cache = new Map();
const order = [];
const listeners = new Map();

function evict() {
  while (order.length > MAX) cache.delete(order.shift());
}

function notify(urn) {
  (listeners.get(urn) || new Set()).forEach((fn) => fn());
}

function subscribe(urn, fn) {
  if (!listeners.has(urn)) listeners.set(urn, new Set());
  listeners.get(urn).add(fn);
  return () => listeners.get(urn)?.delete(fn);
}

function resolve(urn) {
  if (cache.has(urn)) return;
  cache.set(urn, { data: null, status: 'loading' });
  order.push(urn);
  evict();

  fetch(`/api/memory/${encodeURIComponent(urn)}`, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then((data) => { cache.set(urn, { data, status: 'ok' }); notify(urn); })
    .catch(() => { cache.set(urn, { data: null, status: 'miss' }); notify(urn); });
}

export function useMemory(urn) {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!urn) return;
    resolve(urn);
    return subscribe(urn, () => tick((n) => n + 1));
  }, [urn]);

  if (!urn) return { data: null, status: 'miss' };
  if (!cache.has(urn)) return { data: null, status: 'loading' };
  return cache.get(urn);
}
